import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Utensils,
  PlusSquare,
  ChefHat,
  Wallet,
  BookOpen,
  Receipt,
  Users as UsersIcon,
  Settings as SettingsIcon,
  TrendingUp,
  Flame,
  LineChart,
  Menu,
  X,
  Store,
  CalendarDays,
  Package,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { StaggerRoot, StaggerItem } from "@/components/motion";

const NAV = [
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard, roles: ["owner", "manager", "accountant"] },
  { to: "/tables", key: "tables", icon: Utensils, roles: ["owner", "manager", "waiter", "cashier"] },
  { to: "/reservations", key: "reservations", icon: CalendarDays, roles: ["owner", "manager", "cashier", "waiter"] },
  { to: "/new-order", key: "newOrder", icon: PlusSquare, roles: ["owner", "manager", "waiter"] },
  { to: "/kitchen", key: "kitchen", icon: ChefHat, roles: ["owner", "manager", "kitchen", "waiter"] },
  { to: "/cashier", key: "cashier", icon: Wallet, roles: ["owner", "manager", "cashier"] },
  { to: "/menu", key: "menu", icon: BookOpen, roles: ["owner", "manager"] },
  { to: "/inventory", key: "inventory", icon: Package, roles: ["owner", "manager", "accountant"] },
  { to: "/expenses", key: "expenses", icon: Receipt, roles: ["owner", "manager", "accountant"] },
  { to: "/debtors", key: "debtors", icon: UsersIcon, roles: ["owner", "manager", "cashier", "accountant"] },
  { to: "/reports/daily", key: "dailySales", icon: TrendingUp, roles: ["owner", "manager", "accountant", "cashier"] },
  { to: "/reports/best-selling", key: "bestSelling", icon: Flame, roles: ["owner", "manager", "accountant"] },
  { to: "/reports/monthly", key: "monthlyProfit", icon: LineChart, roles: ["owner", "manager", "accountant"] },
  { to: "/users", key: "users", icon: UsersIcon, roles: ["owner", "manager"] },
  { to: "/settings", key: "settings", icon: SettingsIcon, roles: ["owner", "manager"] },
];

export default function Sidebar() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  React.useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!user) return null;
  const items = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <>
      {/* Mobile burger */}
      <button
        data-testid="sidebar-burger"
        onClick={() => setOpen(true)}
        className="md:hidden fixed top-3 right-3 z-50 bg-white border border-gray-200 rounded-xl p-2 shadow-sm"
      >
        <Menu className="w-6 h-6 text-gray-700" />
      </button>

      {/* Backdrop */}
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        data-testid="sidebar"
        className={`fixed top-0 right-0 bottom-0 w-72 bg-white border-l border-gray-200 z-50 transition-transform flex flex-col overflow-hidden
        ${open ? "translate-x-0" : "translate-x-full"} md:translate-x-0`}
      >
        <div className="shrink-0 h-16 px-5 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#071A33] grid place-items-center text-white">
              <Store className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-gray-900 text-base font-heading">
                {t("appName")}
              </div>
              <div className="text-xs text-gray-500">{t("tagline")}</div>
            </div>
          </div>
          <button
            className="md:hidden text-gray-500"
            onClick={() => setOpen(false)}
            data-testid="sidebar-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav
          data-testid="sidebar-nav-scroll"
          className="sidebar-nav-scroll flex-1 min-h-0 overflow-y-auto overscroll-contain thin-scroll p-3"
          aria-label={t("appName")}
        >
          <StaggerRoot delay={0.03}>
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <StaggerItem key={it.to} index={i}>
                  <NavLink
                    to={it.to}
                    data-testid={`nav-${it.key}`}
                    className={({ isActive }) =>
                      `sidebar-nav-item flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium mb-1
                      ${
                        isActive
                          ? "sidebar-nav-item--active bg-[#071A33] text-white hover:text-white"
                          : "text-gray-700 hover:text-gray-700"
                      }`
                    }
                  >
                    <Icon className="w-5 h-5" />
                    <span>{t(it.key)}</span>
                  </NavLink>
                </StaggerItem>
              );
            })}
          </StaggerRoot>
        </nav>
      </aside>
    </>
  );
}
